import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import FooterLogoManagementDialog from './FooterLogoManagementDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FooterLogoManagement = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<any>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch all logos (including inactive)
  const { data: logos, isLoading } = useQuery({
    queryKey: ['admin-footer-logos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_logos')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('footer_logos')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-footer-logos'] });
      queryClient.invalidateQueries({ queryKey: ['footer-logos'] });
      toast({ title: "Logo deleted successfully" });
      setDeleteDialogOpen(false);
    },
    onError: () => {
      toast({ 
        title: "Failed to delete logo", 
        variant: "destructive" 
      });
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: async ({ id, newOrder }: { id: string; newOrder: number }) => {
      const { error } = await supabase
        .from('footer_logos')
        .update({ display_order: newOrder })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-footer-logos'] });
      queryClient.invalidateQueries({ queryKey: ['footer-logos'] });
    },
  });

  const handleReorder = (logo: any, direction: 'up' | 'down') => {
    if (!logos) return;
    
    const currentIndex = logos.findIndex(l => l.id === logo.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= logos.length) return;
    
    const targetLogo = logos[targetIndex];
    
    reorderMutation.mutate({ id: logo.id, newOrder: targetLogo.display_order });
    reorderMutation.mutate({ id: targetLogo.id, newOrder: logo.display_order });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setSelectedLogo(null); setDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Logo
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Preview</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Link</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : logos?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center">No logos found</TableCell>
              </TableRow>
            ) : (
              logos?.map((logo, index) => (
                <TableRow key={logo.id}>
                  <TableCell>
                    <img 
                      src={logo.image_url} 
                      alt={logo.alt_text}
                      className="w-16 h-12 object-contain"
                    />
                  </TableCell>
                  <TableCell className="font-medium">{logo.name}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                    {logo.link_url || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{logo.display_order}</span>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReorder(logo, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReorder(logo, 'down')}
                          disabled={index === logos.length - 1}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={logo.is_active ? "default" : "secondary"}>
                      {logo.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedLogo(logo); setDialogOpen(true); }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setSelectedLogo(logo); setDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FooterLogoManagementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        logo={selectedLogo}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Logo</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedLogo?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate(selectedLogo?.id)}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FooterLogoManagement;